import {
  IconDots,
  IconEdit,
  IconKey,
  IconTrash,
  IconUserCheck,
  IconUserCog,
  IconUserX,
} from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { AdminUser } from "@/lib/admin-types";
import { authClient } from "@/lib/auth-client";

type UserActionsProps = {
  user: AdminUser;
  onEdit: () => void;
  onRefresh: () => void;
};

type ActionType = "ban" | "unban" | "delete" | null;

export function UserActions({ user, onEdit, onRefresh }: UserActionsProps) {
  const { t } = useTranslation();
  const [pendingAction, setPendingAction] = useState<ActionType>(null);

  // Ban user mutation
  const banUserMutation = useMutation({
    mutationFn: async (banReason?: string) => {
      const response = await authClient.admin.banUser({
        userId: user.id,
        banReason,
      });
      if (!response.data) {
        throw new Error("Failed to ban user");
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("admin.user.actions.banSuccess"));
      onRefresh();
      setPendingAction(null);
    },
    onError: (error) => {
      toast.error(t("admin.user.actions.banFailed", { error: error.message }));
      setPendingAction(null);
    },
  });

  // Unban user mutation
  const unbanUserMutation = useMutation({
    mutationFn: async () => {
      const response = await authClient.admin.unbanUser({
        userId: user.id,
      });
      if (!response.data) {
        throw new Error("Failed to unban user");
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("admin.user.actions.unbanSuccess"));
      onRefresh();
      setPendingAction(null);
    },
    onError: (error) => {
      toast.error(t("admin.user.actions.unbanFailed", { error: error.message }));
      setPendingAction(null);
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async () => {
      const response = await authClient.admin.removeUser({
        userId: user.id,
      });
      if (!response.data) {
        throw new Error("Failed to delete user");
      }
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("admin.user.actions.deleteSuccess"));
      onRefresh();
      setPendingAction(null);
    },
    onError: (error) => {
      toast.error(t("admin.user.actions.deleteFailed", { error: error.message }));
      setPendingAction(null);
    },
  });

  const handleBanUser = () => {
    banUserMutation.mutate("Banned by administrator");
  };

  const handleUnbanUser = () => {
    unbanUserMutation.mutate();
  };

  const handleDeleteUser = () => {
    deleteUserMutation.mutate();
  };

  const isLoading =
    banUserMutation.isPending || unbanUserMutation.isPending || deleteUserMutation.isPending;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="h-8 w-8 p-0" disabled={isLoading} variant="ghost">
            <span className="sr-only">Open menu</span>
            <IconDots className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t("admin.user.actions.menuLabel")}</DropdownMenuLabel>
          <DropdownMenuItem onClick={onEdit}>
            <IconEdit className="mr-2 h-4 w-4" />
            {t("admin.user.actions.edit")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              /* TODO: Implement role management */
            }}
          >
            <IconUserCog className="mr-2 h-4 w-4" />
            {t("admin.user.actions.manageRoles")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              /* TODO: Implement password reset */
            }}
          >
            <IconKey className="mr-2 h-4 w-4" />
            {t("admin.user.actions.resetPassword")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {user.banned ? (
            <DropdownMenuItem className="text-green-600" onClick={() => setPendingAction("unban")}>
              <IconUserCheck className="mr-2 h-4 w-4" />
              {t("admin.user.actions.unbanUser")}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem className="text-orange-600" onClick={() => setPendingAction("ban")}>
              <IconUserX className="mr-2 h-4 w-4" />
              {t("admin.user.actions.banUser")}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-600" onClick={() => setPendingAction("delete")}>
            <IconTrash className="mr-2 h-4 w-4" />
            {t("admin.user.actions.deleteUser")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Ban Confirmation Dialog */}
      <AlertDialog onOpenChange={() => setPendingAction(null)} open={pendingAction === "ban"}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.user.actions.banConfirm.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.user.actions.banConfirm.description", { name: user.name || user.email })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("dataTable.deleteConfirmation.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleBanUser}
            >
              {t("admin.user.actions.banUser")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unban Confirmation Dialog */}
      <AlertDialog onOpenChange={() => setPendingAction(null)} open={pendingAction === "unban"}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.user.actions.unbanConfirm.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.user.actions.unbanConfirm.description", { name: user.name || user.email })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("dataTable.deleteConfirmation.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnbanUser}>
              {t("admin.user.actions.unbanUser")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog onOpenChange={() => setPendingAction(null)} open={pendingAction === "delete"}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.user.actions.deleteConfirm.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.user.actions.deleteConfirm.description", { name: user.name || user.email })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("dataTable.deleteConfirmation.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteUser}
            >
              {t("admin.user.actions.deleteUser")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
